// assets/collections-filter.js

/**
 * Page regions replaced when the visitor switches collection.
 * The filter sidebar goes last because the click that triggered the swap
 * originated inside it.
 */
const GX_SWAP = [
  '.main_collection_title',
  '.main_collection_grid_wrapper',
  '.main_collection_vertical_filters_wrapper',
];

class GlixorCollectionsFilter extends HTMLElement {
  connectedCallback() {
    if (this.gxReady) return;

    this.list = this.querySelector('[data-collections-list]');
    if (!this.list) return;

    this.items = Array.from(this.list.children);
    if (!this.items.length) {
      this.closest('details')?.remove();
      return;
    }

    this.initial = parseInt(this.dataset.initial, 10) || 10;
    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);

    if (this.items.length > this.initial) this.buildOverflow();

    GlixorCollectionsFilter.bindHistory();
    this.gxReady = true;
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.toggle?.removeEventListener('click', this.onToggle);
  }

  /* ---------- Read More / Show Less ---------- */

  buildOverflow() {
    const wrap = document.createElement('div');
    wrap.className = 'glixor-collections-filter__overflow';

    const ul = document.createElement('ul');
    ul.className = 'glixor-collections-filter__list';
    this.items.slice(this.initial).forEach((li) => ul.appendChild(li));
    wrap.appendChild(ul);

    this.toggle = document.createElement('button');
    this.toggle.type = 'button';
    this.toggle.className = 'glixor-collections-filter__more';
    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.textContent = this.dataset.labelMore || 'Read More';

    this.overflow = wrap;
    this.appendChild(wrap);
    this.appendChild(this.toggle);

    this.onToggle = this.onToggle.bind(this);
    this.toggle.addEventListener('click', this.onToggle);

    // Keep a checked collection visible if it fell past the fold.
    if (ul.querySelector('input:checked')) {
      this.expand(true);
    } else {
      wrap.hidden = true;
    }
  }

  onToggle() {
    this.expand(this.toggle.getAttribute('aria-expanded') !== 'true');
  }

  expand(open) {
    if (!this.overflow || !this.toggle) return;

    if (open) this.overflow.hidden = false;
    void this.overflow.offsetHeight; // start frame for the transition

    this.overflow.classList.toggle('is-open', open);
    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.textContent = open ? this.dataset.labelLess : this.dataset.labelMore;

    if (!open) {
      const done = () => {
        if (this.toggle.getAttribute('aria-expanded') !== 'true') this.overflow.hidden = true;
        this.overflow.removeEventListener('transitionend', done);
      };
      this.overflow.addEventListener('transitionend', done);
    }
  }

  /* ---------- selection ---------- */

  onChange(event) {
    const box = event.target;
    if (!box.matches('input[data-collection-url]')) return;

    const url = box.checked ? box.dataset.collectionUrl : box.dataset.collectionResetUrl;
    if (!url) return;

    // Single-select: a collection is a location, not a refinement.
    this.querySelectorAll('input[data-collection-url]').forEach((other) => {
      if (other !== box) other.checked = false;
    });

    GlixorCollectionsFilter.navigate(url, true);
  }

  /* ---------- AJAX swap ---------- */

  static setBusy(state) {
    document.querySelectorAll('.loader').forEach((el) => el.classList.toggle('hidden', !state));
    document
      .querySelectorAll('glixor-collections-filter input[data-collection-url]')
      .forEach((el) => {
        el.disabled = state;
      });
  }

  static async navigate(url, push) {
    // Abort an in-flight swap so rapid clicks can't land out of order.
    GlixorCollectionsFilter.controller?.abort();
    const controller = new AbortController();
    GlixorCollectionsFilter.controller = controller;

    GlixorCollectionsFilter.setBusy(true);

    try {
      const res = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

      GX_SWAP.forEach((selector) => {
        const next = doc.querySelector(selector);
        const current = document.querySelector(selector);
        if (next && current) current.replaceWith(next);
      });

      // Swap the drawer's form only. Replacing #mobile_filters would destroy
      // the close button node the theme's drawer JS is bound to.
      const nextForm = doc.querySelector('#mobile_filters_form');
      const curForm = document.querySelector('#mobile_filters_form');
      if (nextForm && curForm) curForm.replaceWith(nextForm);

      const nextActive = doc.querySelector('.mob_active_values_wrapper');
      const curActive = document.querySelector('.mob_active_values_wrapper');
      if (nextActive && curActive) {
        curActive.replaceWith(nextActive);
      } else if (nextActive && !curActive) {
        document.querySelector('.mobile_filters_content')?.before(nextActive);
      } else if (!nextActive && curActive) {
        curActive.remove();
      }

      const title = doc.querySelector('title');
      if (title) document.title = title.textContent;

      if (push) window.history.pushState({ gxCollections: true }, '', url);

      document
        .querySelector('.main_collection_inner')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[glixor] collection swap failed:', err);
      window.location.assign(url); // hard fallback — never strand the visitor
      return;
    } finally {
      if (GlixorCollectionsFilter.controller === controller) {
        GlixorCollectionsFilter.setBusy(false);
        GlixorCollectionsFilter.controller = null;
      }
    }
  }

  static bindHistory() {
    if (GlixorCollectionsFilter.historyBound) return;
    GlixorCollectionsFilter.historyBound = true;

    window.addEventListener('popstate', (event) => {
      if (!event.state?.gxCollections) return;
      GlixorCollectionsFilter.navigate(window.location.href, false);
    });
  }
}

GlixorCollectionsFilter.controller = null;
GlixorCollectionsFilter.historyBound = false;

if (!customElements.get('glixor-collections-filter')) {
  customElements.define('glixor-collections-filter', GlixorCollectionsFilter);
}

/**
 * Delegated drawer close. Bound to document, so it survives any DOM swap
 * and any future markup change inside the drawer. No-op when the theme's
 * own handler already closed it.
 */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('.mobile_filter_drawer .drawer_close_btn');
  if (!btn) return;

  const drawer = btn.closest('.drawer-wrapper');
  if (!drawer || drawer.hasAttribute('inert')) return;

  drawer.setAttribute('inert', '');
  drawer.setAttribute('aria-hidden', 'true');
  drawer.classList.remove('active', 'is-open', 'open');
  document.body.classList.remove('drawer_open', 'overflow_hidden', 'no-scroll');
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document
    .querySelector('.mobile_filter_drawer:not([inert])')
    ?.querySelector('.drawer_close_btn')
    ?.click();
});